import {
  Button,
  Select,
  TextArea,
  TextField,
  Typography,
} from "@suraasa/placebo-ui"
import { NarrationForm, ToolType } from "api/resources/aiTools/types"
import CreatingFor from "features/AItools/components/CreatingFor"
import Paper from "features/AItools/components/Paper"
import {
  BLOOMS_TAXONOMY_OPTIONS,
  DURATION_MINUTES,
  getToolName,
  setLastUsedToolType,
} from "features/AItools/helper"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { Link } from "react-router-dom"

import { NarrationReturnType } from "../useNarration"

const toolType = ToolType.narration
type AutoFillableFields = "grade" | "curriculum" | "subject" | "country"

type Props = Pick<
  NarrationReturnType,
  "generateTool" | "formData" | "preferredPromptData"
>

const BasicForm = ({ generateTool, formData, preferredPromptData }: Props) => {
  const {
    control,
    register,
    setError,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NarrationForm>({
    defaultValues: formData || {
      grade: preferredPromptData?.grade || "",
      curriculum: preferredPromptData?.curriculum || "",
      subject: preferredPromptData?.subject || "",
      country: preferredPromptData?.country || "",
    },
  })

  const { checkGenerationAvailability, aiToolsUsageQuery } =
    useSuraasaPlusContext()

  useEffect(() => {
    const prefillFields: AutoFillableFields[] = [
      "grade",
      "curriculum",
      "subject",
      "country",
    ]

    if (prefillFields.every(field => preferredPromptData?.[field])) {
      setEditing(false)
    }

    reset(
      formData || {
        grade: preferredPromptData?.grade || "",
        curriculum: preferredPromptData?.curriculum || "",
        subject: preferredPromptData?.subject || "",
        country: preferredPromptData?.country || "",
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredPromptData])

  const onSubmit = handleSubmit(async data => {
    setLastUsedToolType({ toolType: toolType })

    checkGenerationAvailability(async () => {
      await generateTool.mutateAsync({ data, setError })
      aiToolsUsageQuery?.refetch()
    })
  })

  const [isEditing, setEditing] = useState(true)

  const subject = watch("subject")
  const country = watch("country")
  const grade = watch("grade")
  const curriculum = watch("curriculum")

  return (
    <Paper>
      <form onSubmit={onSubmit} className="flex w-full flex-col gap-2">
        {!isEditing && (
          <CreatingFor
            country={country}
            curriculum={curriculum}
            grade={grade}
            subject={subject}
            onEditClick={() => setEditing(true)}
          />
        )}
        {(!grade || !curriculum || isEditing) && (
          <div className="flex flex-wrap gap-2 md:flex-nowrap">
            {(!grade || isEditing) && (
              <TextField
                label="Class/Grade"
                placeholder="Ex: 12th Grade"
                errors={errors.grade?.message}
                {...register("grade", {
                  required: { value: true, message: "Required" },
                })}
              />
            )}
            {(!curriculum || isEditing) && (
              <TextField
                {...register("curriculum", {
                  required: { value: true, message: "Required" },
                })}
                label="Curriculum"
                placeholder="Ex: CBSE"
                errors={errors.curriculum?.message}
              />
            )}
          </div>
        )}
        {(!subject || !country || isEditing) && (
          <div className="flex flex-wrap gap-2 md:flex-nowrap">
            {(!subject || isEditing) && (
              <TextField
                label="Subject"
                placeholder="Ex: Arts and Craft"
                errors={errors.subject?.message}
                {...register("subject", {
                  required: { value: true, message: "Required" },
                })}
              />
            )}
            {(!country || isEditing) && (
              <TextField
                {...register("country", {
                  required: { value: true, message: "Required" },
                })}
                label="Country"
                placeholder="Ex: India"
                errors={errors.country?.message}
                helperText={`We use this to tailor the ${getToolName(
                  toolType
                )} to your region`}
              />
            )}
          </div>
        )}
        <TextField
          label="topic"
          placeholder="Ex: Rajasthani Paintings"
          errors={errors.topic?.message}
          {...register("topic", {
            required: { value: true, message: "Required" },
          })}
        />
        <TextArea
          rows={3}
          label="Objective/Description"
          placeholder="Ex: To understand the history of Rajasthani paintings and its significance in Indian art."
          errors={errors.lessonPlanObjectives?.message}
          {...register("lessonPlanObjectives", {
            required: { value: true, message: "Required" },
          })}
        />
        <div className="flex flex-wrap gap-2 md:flex-nowrap">
          <Controller
            control={control}
            name="duration"
            render={({ field: { onChange, onBlur, value } }) => (
              <Select
                label="Class Duration"
                errors={errors.duration?.message}
                options={DURATION_MINUTES}
                placeholder="Ex: 60 Minutes"
                value={DURATION_MINUTES.find(c => c.value === value)}
                mountOnBody
                onBlur={onBlur}
                onChange={val => onChange(val?.value)}
              />
            )}
            rules={{ required: { value: true, message: "Required" } }}
          />

          <div className="flex flex-[50%] shrink-0 flex-col">
            <Controller
              control={control}
              name="bloomTaxonomyLevel"
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  isMulti
                  closeMenuOnSelect={false}
                  label="Bloom's Taxonomy Level"
                  errors={errors.bloomTaxonomyLevel?.message}
                  options={BLOOMS_TAXONOMY_OPTIONS}
                  placeholder="Ex: Evaluate, Create"
                  value={
                    value &&
                    BLOOMS_TAXONOMY_OPTIONS.filter(c => value.includes(c.value))
                  }
                  mountOnBody
                  onBlur={onBlur}
                  onChange={val => onChange(val.map(x => x.value))}
                />
              )}
              rules={{ required: { value: true, message: "Required" } }}
            />
            <Link
              to="https://www.suraasa.com/blog/learning-taxonomies"
              target="_blank"
            >
              <Typography
                className="mt-0.5 text-interactive-500 hover:underline"
                variant="smallBody"
              >
                What is Bloom&apos;s Taxonomy?
              </Typography>
            </Link>
          </div>
        </div>
        <Button type="submit" color="black" loading={isSubmitting}>
          Generate {getToolName(toolType)}
        </Button>
      </form>
    </Paper>
  )
}

export default BasicForm
